import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { RegistrationConfirmationCode } from '@adornis/buchungsheld-db/entities/RegistrationConfirmationCode.js';
import { RegistrationTooManyAttemptsError } from '@adornis/buchungsheld-db/errors/RegistrationTooManyAttemptsError.js';
import { RegistrationWrongCodeError } from '@adornis/buchungsheld-db/errors/RegistrationWrongCodeError.js';
import { UserNotFoundError } from '@adornis/buchungsheld-db/errors/UserNotFoundError.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import './bh-input-auth-code.js';
import type { BHInputAuthCode } from './bh-input-auth-code.js';

/**
 * @element bh-component-action-otp-login
 */
@customElement('bh-component-action-otp-login')
export class BhComponentActionOtpLogin extends BuchungsheldLitElement {
  @property({ attribute: 'user-id' }) userID?: string;
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: 'email' }) email?: string;

  @state() private _status?: string;
  @state() private _authCode?: string;

  private static lastAutomaticOTPSend?: Map<string, DateTime>;
  private _inputAuthCode = createRef<BHInputAuthCode>();

  override connectedCallback(): void {
    super.connectedCallback();

    // need the lastAutomaticOTPSend timestamp to avoid sending too many login tokens
    // caused by a short loading state when user is logging in before error of registrationController gets cleared
    // causing this component to be reattached, thus calling connectedCallback again
    const constr = this.constructor as typeof BhComponentActionOtpLogin;
    if (
      !constr.lastAutomaticOTPSend ||
      !constr.lastAutomaticOTPSend.get(`(${this.userID},${this.email})`) ||
      constr.lastAutomaticOTPSend.get(`(${this.userID},${this.email})`)! < DateTime.now().minus({ minutes: 5 })
    ) {
      void this._sendCode();
      constr.lastAutomaticOTPSend?.set(`(${this.userID},${this.email})`, DateTime.now());
    }
  }

  override render() {
    return html`
      <x-flex center crossaxis-center h100 w100>
        <x-flex
          ${css({ background: '#fff', borderRadius: this.sizes.borderRadius })}
          space="md"
          crossaxis-center
          padding="xl"
        >
          <x-icon
            ${css({
              padding: this.spacing.md,
              borderRadius: '50%',
              background: this.colors.accent,
              color: this.colors.white,
              fontSize: '1.5em',
            })}
          >
            shield
          </x-icon>
          <tr-h3 bold> ${this._translation.translate('OTP', 'title')} </tr-h3>
          <tr-text ${css({ width: 'min(300px, 100%)', textAlign: 'center' })}>
            ${this._translation.translate('OTP', 'subtitle')}.
          </tr-text>
          ${this._status
            ? html`
                <tr-text ${css({ width: 'min(300px, 100%)', textAlign: 'center', color: this.colors.tone.error })}>
                  ${this._status}
                </tr-text>
              `
            : nothing}
          <bh-input-auth-code
            ${ref(this._inputAuthCode)}
            @value-changed=${(e: ValueEvent<string>) => (this._authCode = e.detail.value)}
            @enter=${() => this._submitCode()}
          ></bh-input-auth-code>
          <x-button @click=${() => this._submitCode()}>
            <tr-h4 bold> ${this._translation.translate('OTP', 'check')} </tr-h4>
          </x-button>
          <x-button mode="text" icon="refresh" @click=${() => this._sendCode(true)}>
            ${this._translation.translate('OTP', 'resendCode')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _sendCode(resend = false) {
    if (this.userID || this.email) {
      try {
        await XDialog.waitFor(RegistrationConfirmationCode.sendOTP(this.userID, this.email, this.institutionID)());
        if (resend) {
          void XSnackbar.show(this._translation.translate('REGISTRATION', 'successfullyResendConfirmationCode'));
        }
      } catch (err) {
        let errMsg = '';
        let errDsc = '';
        switch ((err as any)?._class) {
          case UserNotFoundError._class: {
            errMsg = 'Benutzer existiert nicht';
            errDsc = this._translation.translate('OTP', 'mail_login_user_non_existent_message') as string;
            break;
          }

          default: {
            errMsg = 'Es gab ein Problem beim Versenden des OTP';
            errDsc = resend
              ? (this._translation.translate('REGISTRATION', 'problemOccuredResendingOTP') as string)
              : (this._translation.translate('OTP', 'problemOccuredSendingOTP') as string);
          }
        }

        logger.error({ err }, errMsg);
        void XSnackbar.show(errDsc);
      }
    }
  }

  private async _submitCode() {
    const authCode = this._inputAuthCode.value ? this._inputAuthCode.value.getCurrentValues() : this._authCode;
    if (!authCode) {
      logger.error({ authCode: authCode }, 'Did not enter auth code');
      return XSnackbar.show(this._translation.translate('REGISTRATION', 'noOTPEntered'));
    }
    try {
      await XDialog.waitFor(RegistrationConfirmationCode.loginWithOTP(authCode));
      this.dispatchEvent(new CustomEvent('login'));
    } catch (error: any) {
      logger.error({ err: error }, 'Es gab ein Problem beim Login mit OTP');
      this._handleError(error);
    }
  }

  private _handleError(error: any) {
    switch (error?._class) {
      case RegistrationWrongCodeError._class:
      case RegistrationTooManyAttemptsError._class:
        this._status = this._translation.translate('errors', error._class, 'explanation') as unknown as string;
        break;
      default:
    }
  }
}
